import React, {useEffect, useState, useCallback} from 'react';
import styled from 'styled-components';

import {getUnitLabel} from '../FormBuilderScreen/shared';
import Icon from '../../components/Icon';
import {Tag, AddTagButton, TagsContainer} from '../../components/TagComponents';
import {useApi} from '../../lib/api';
import {useCredentials} from '../../lib/user';
import * as tagHelpers from '../../lib/tag-helpers';

export const PAGE_SIZE = 50;

const TYPE_LABELS = {
  RECEIVE: 'Received',
  USE: 'Used',
  ADJUST: 'Adjusted',
  PRODUCE: 'Produced',
  SHIP: 'Shipped',
  TRANSFER: 'Transferred'
};

const STATUS_LABELS = {
  NEGATIVE: 'Negative balance',
  CONFLICT: 'Unit conflict',
  UNRESOLVED_ITEM: 'Item not identified',
  ERROR: 'Posting error'
};

export function getTypeLabel(type) {
  return TYPE_LABELS[type] || type || 'Unknown';
}

//POSTED is the normal case, so it has no label and gets no badge
export function getStatusLabel(status) {
  return STATUS_LABELS[status] || null;
}

export function formatQty(value) {
  if (value === null || value === undefined || isNaN(value)) {
    return '';
  }

  //the same as numbers everywhere else in the dashboard. the ledger stores 6 decimals so conversions add up exactly,
  //but nobody reads stock to the microgram
  return Number(value).toLocaleString();
}

export function formatSignedQty(value) {
  const formatted = formatQty(value);
  return Number(value) > 0 ? `+${formatted}` : formatted;
}

export function withUnit(formattedQty, unit) {
  const label = getUnitLabel(unit);
  return label ? `${formattedQty} ${label}` : formattedQty;
}

//UNRESOLVED_ITEM and ERROR records may have no item, so fall back to the item and lot as typed on the answer
export function getTransactionItemName(transaction) {
  return transaction.itemName || transaction.answeredItem || 'Unknown item';
}

export function getTransactionLotCode(transaction) {
  return transaction.itemId ? transaction.lotCode : (transaction.lotCode || transaction.answeredLot);
}

export function getLotLabel(lotCode) {
  return lotCode ? `Lot ${lotCode}` : 'No lot';
}

//occurredAt is the device time the answer was given; postedAt is when the server applied it
export function getTransactionDate(transaction) {
  const value = transaction.occurredAt || transaction.postedAt;
  return value ? new Date(value) : null;
}

export function formatDateTime(date) {
  return date ? date.toLocaleString() : '';
}

//Loads one page of a Spring Page resource. The previous result is kept while the next page loads so the list doesn't
//flash empty; only the very first load has no result to show.
export function usePagedResource(load, deps, enabled = true) {
  const [page, setPage] = useState(0);
  const [reloadCount, setReloadCount] = useState(0);
  const [state, setState] = useState({loading: enabled, error: null, result: null});

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    let cancelled = false;
    setState(prevState => ({...prevState, loading: true, error: null}));

    load(page).then(
      result => {
        if (cancelled) {
          return;
        }
        //a reload can empty the last page, e.g. once its only exception is resolved, so step back to one with rows
        if (page > 0 && result && result.content && result.content.length < 1 && result.totalElements > 0) {
          setPage(Math.max(0, Math.ceil(result.totalElements / result.size) - 1));
          return;
        }
        setState({loading: false, error: null, result});
      },
      error => {
        if (!cancelled) {
          console.error(error);
          setState({loading: false, error, result: null});
        }
      }
    );

    return () => {
      cancelled = true;
    };
  }, [page, reloadCount, enabled, ...deps]);

  const reload = useCallback(() => setReloadCount(count => count + 1), []);

  return {...state, page, setPage, reload};
}

//the forms and inventory tags the toolbars filter by. the tags are reloaded after an item's tags change, since that
//can create new ones
export function useInventoryFilterOptions() {
  const api = useApi();
  const credentials = useCredentials();
  const [forms, setForms] = useState([]);
  const [tags, setTags] = useState([]);
  //a tag filter in the url is sent to the api by id, so the lists wait for this before their first load
  const [tagsLoaded, setTagsLoaded] = useState(false);

  const reloadTags = useCallback(() => {
    return tagHelpers.fetchAvailableTags(credentials, 'inventory')
      .then(setTags)
      .finally(() => setTagsLoaded(true));
  }, [credentials]);

  useEffect(() => {
    api.getInventoryForms().then(setForms, error => console.error(error));
    reloadTags();
  }, []);

  return {forms, tags, tagsLoaded, reloadTags};
}

//whether a list can load for the tag in its url: straight away without one, once the tags are known with one. a
//tag that no longer exists is dropped from the url, so the list is never shown unfiltered under a filter it claims
export function useTagFilterReady(tag, tagId, tagsLoaded, setUrlState) {
  const unknownTag = tag !== 'all' && tagsLoaded && !tagId;

  useEffect(() => {
    if (unknownTag) {
      setUrlState({tag: 'all'});
    }
  }, [unknownTag]);

  return tag === 'all' || (tagsLoaded && !!tagId);
}

//the tag filter is kept in the url by name, like the forms list, and sent to the api by id
export function getTagId(tags, tagName) {
  if (!tagName || tagName === 'all') {
    return null;
  }
  const tag = tags.find(t => tagHelpers.getTagName(t) === tagName);
  return tag ? tag.id : null;
}

//the inventory tags on an item as the forms list shows them: click a tag to filter by it, + to change them
export function ItemTags({tags, onSelect, onEdit}) {
  const hasTags = !!(tags && tags.length > 0);
  if (!hasTags) {
    return null;
  }

  return (
    <TagsContainer variant="form" hasContent={hasTags}>
      {tags.map(tag => {
        const tagName = tagHelpers.getTagName(tag);
        return <Tag key={tagName} onClick={onSelect ? () => onSelect(tagName) : undefined}>{tagName}</Tag>;
      })}
      {onEdit && (
        <AddTagButton onClick={onEdit}>
          <Icon name="plus" size="1em" />
        </AddTagButton>
      )}
    </TagsContainer>
  );
}

//the text in a list's search box, and the search sent to the api once typing pauses
export function useDebouncedSearch(delay = 300) {
  const [searchText, setSearchText] = useState('');
  const [search, setSearch] = useState('');

  useEffect(() => {
    const handle = setTimeout(() => setSearch(searchText.trim()), delay);
    return () => clearTimeout(handle);
  }, [searchText, delay]);

  return {searchText, setSearchText, search};
}

function hasSubmissionPath(source) {
  return !!source && !!source.templateId && !!source.sheetId && !!source.sheetRowId;
}

//the View Data path of the submission a record came from. adjustments have none, and neither does a record from a
//form the user can't see
export function getSubmissionPath(transaction) {
  const {source, sourceHidden} = transaction;
  return !sourceHidden && hasSubmissionPath(source)
    ? `/sheet/${source.templateId}/${source.sheetId}/data/${source.sheetRowId}`
    : null;
}

//a record that needs attention gets the same amber warning icon the forms list uses for imports with errors
export function AttentionIcon({title}) {
  return (
    <AttentionIconContainer title={title}>
      <Icon color="#FFB300" name="alert-triangle" size="1em" />
    </AttentionIconContainer>
  );
}

const AttentionIconContainer = styled.span`
  display: inline-flex;
  vertical-align: -0.125em;
  margin-left: 0.375rem;
`;

//the amount on the right of a list row, kept clear of the row's buttons
export const RowAmount = styled.strong`
  display: block;
  margin-right: 1.25rem;
  white-space: nowrap;
`;

export const Negative = styled.span`
  color: #B4131A;
`;

export const PaginationContainer = styled.div`
  padding: 1rem;
  display: flex;
  justify-content: center;
  border-top: 1px solid #e4e4e4;
`;
